import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Plus, MessageSquare, Plane, ArrowLeft, CircleDot } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { NewConversationDialog, ConversationThread, fmtChatTime } from "@/pages/chat/ChatPage";

export default function ReservaChat({ reserva, trip }) {
  const [selectedId, setSelectedId] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const label =
    [trip?.main_traveler, reserva?.service || reserva?.provider_name].filter(Boolean).join(" · ") ||
    (reserva?.id || "").slice(0, 8);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["chat-reserva-list", reserva.id],
    queryFn: async () => (await api.get("/chat/conversations", { params: { reserva_id: reserva.id } })).data,
    refetchInterval: 6000,
  });
  const items = data?.items || [];

  if (selectedId) {
    return (
      <Card className="overflow-hidden" data-testid="reserva-chat-thread">
        <div className="flex items-center gap-2 border-b p-2">
          <Button variant="ghost" size="sm" onClick={() => setSelectedId(null)} data-testid="rt-chat-back">
            <ArrowLeft className="mr-1.5 h-4 w-4" />Volver a la lista
          </Button>
        </div>
        <div className="h-[520px]">
          <ConversationThread conversationId={selectedId} />
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-3" data-testid="reserva-chat">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">Conversaciones del equipo vinculadas a esta reserva.</p>
        <Button size="sm" onClick={() => setNewOpen(true)} data-testid="rt-chat-new-btn">
          <Plus className="mr-1.5 h-4 w-4" />Nueva conversación
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center gap-2 p-10 text-center text-muted-foreground" data-testid="reserva-chat-empty">
            <MessageSquare className="h-9 w-9 opacity-30" />
            <p className="text-sm">No hay conversaciones para esta reserva todavía.</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="divide-y" data-testid="reserva-chat-list">
            {items.map((c) => (
              <button
                key={c.id}
                onClick={() => setSelectedId(c.id)}
                className="flex w-full flex-col gap-1 px-3.5 py-3 text-left transition-colors hover:bg-muted/50"
                data-testid={`reserva-chat-item-${c.id}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="flex items-center gap-1.5 truncate text-sm font-medium">
                    {c.unread && <CircleDot className="h-3 w-3 shrink-0 text-primary" />}
                    {c.subject}
                  </span>
                  <span className="shrink-0 text-[10px] text-muted-foreground">{fmtChatTime(c.last_message_at)}</span>
                </div>
                <span className="truncate text-[11px] text-muted-foreground">{(c.participants || []).map((p) => p.name).join(", ")}</span>
                {c.last_message_preview && (
                  <span className={`truncate text-xs ${c.unread ? "font-medium text-foreground" : "text-muted-foreground"}`}>
                    {c.last_message_by ? `${c.last_message_by.split(" ")[0]}: ` : ""}{c.last_message_preview}
                  </span>
                )}
                {c.solved && <Badge variant="outline" className="w-fit border-emerald-200 bg-emerald-50 text-[9px] text-emerald-700">Resuelto</Badge>}
              </button>
            ))}
          </div>
        </Card>
      )}

      <NewConversationDialog
        open={newOpen}
        onClose={() => setNewOpen(false)}
        presetReserva={{ id: reserva.id, label }}
        onCreated={(c) => { refetch(); setSelectedId(c.id); }}
      />
    </div>
  );
}
